import React, { useState } from 'react';
import TableRow from '@mui/material/TableRow';
import TableCell from 'src/components/Table/TableCell';
import PropTypes from 'prop-types';
import selectFormatDate from 'src/utils/selectDateFormat';
import dateFormatType from 'src/utils/dateFormatType';
import { formatAmountWithDecimals } from 'src/utils/formatAmout';
import OverflowTipography from 'src/components/OverflowTipography';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MoreMenu from 'src/components/MoreMenu/MoreMenu';
import { CFDIListActions } from 'src/utils/constants/cfdi';
import { cancelCfdiAction } from 'src/store/actions/cfdiActions';
import DocumentViewerDialog from 'src/components/DocumentViewer/DocumentViewerDialog';
import { useDispatch } from 'react-redux';
import { getCFDIListOptions, selectIconFromCfdiStatus } from 'src/utils/cfdi';
import SendEmailDialog from './SendEmailDialog';
import SendWhatsappDialog from './SendWhatsappDialog';
import CancelCFDIDialog from './CancelCFDIDialog';

const CFDIIssuedRow = ({
  id,
  createdAt,
  folio,
  series,
  type,
  amount,
  iva,
  amountIvaIncluded,
  client,
  status,
  document, 
  uuid,
  transaction,
  relatedDocuments
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [openOption, setOpenOption] = useState('');

  const handleCloseDialog = () => {
    setOpenOption('');
  };

  const handleCancelCdfi = values => {
    dispatch(cancelCfdiAction({ id, ...values }))
      .then(() => {
        handleCloseDialog();
      })
      .catch(() => {
        handleCloseDialog();
      });
  };

  const relatedInvoiceData = {
    invoiceType: type,
    category: transaction.category,
    contractId: transaction.contractId,
    clientId: client.id,
    amount: amountIvaIncluded,
    clientName: client.name,
    folio,
    paymentDate: createdAt,
    relatedDocuments,
    comment: transaction.comment
  };

  const handleOpenDialog = option => {
    switch (option) {
      case CFDIListActions.VIEW_PDF:
        setOpenOption(CFDIListActions.VIEW_PDF);
        break;
      case CFDIListActions.SEND_EMAIL:
        setOpenOption(CFDIListActions.SEND_EMAIL);
        break;
      case CFDIListActions.SEND_WHATSAPP:
        setOpenOption(CFDIListActions.SEND_WHATSAPP);
        break;
      case CFDIListActions.CANCEL:
        setOpenOption(CFDIListActions.CANCEL);
        break;
      default:
        break;
    }
  };

  return (
    <TableRow hover align="center">
      <TableCell
        style={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}
        align="center"
      >
        {selectFormatDate(createdAt, dateFormatType.DAY)}
      </TableCell>
      <TableCell align="center">{folio}</TableCell>
      <TableCell align="left">
        <OverflowTipography title={client?.name} maxWidth="100%">
          <Link to={`/clients/detail/${client?.id}`}>{client?.name}</Link>
        </OverflowTipography>
      </TableCell>
      <TableCell align="center">{t(`ENUMS.INVOICE_TYPES.${type}`)}</TableCell>
      <TableCell align="center">{series}</TableCell>
      <TableCell align="center">
        {formatAmountWithDecimals(amount)}
      </TableCell>
      <TableCell align="center">{formatAmountWithDecimals(iva)}</TableCell>
      <TableCell align="center">
        {formatAmountWithDecimals(amountIvaIncluded)}
      </TableCell>
      <TableCell align="center">{selectIconFromCfdiStatus(status)}</TableCell>
      <TableCell align="right">
        <MoreMenu
          deleteDisabled
          editDisabled
          key={folio}
          options={getCFDIListOptions(document, client, type, status)}
          handleClickOption={handleOpenDialog}
        />
        {openOption === CFDIListActions.VIEW_PDF && document.documentUrl && (
          <DocumentViewerDialog
            documentUrl={document.documentUrl}
            open={openOption === CFDIListActions.VIEW_PDF}
            onClose={handleCloseDialog}
            fileName={`${uuid}`}
          />
        )}
      </TableCell>
      <CancelCFDIDialog
        open={openOption === CFDIListActions.CANCEL}
        onClose={handleCloseDialog}
        onSubmit={handleCancelCdfi}
        createdAt={createdAt}
        amount={amountIvaIncluded}
        relatedInvoiceData={relatedInvoiceData}
      />
      <SendEmailDialog
        open={openOption === CFDIListActions.SEND_EMAIL}
        onClose={handleCloseDialog}
        id={id}
        clientId={client?.id}
      />
      <SendWhatsappDialog
        open={openOption === CFDIListActions.SEND_WHATSAPP}
        onClose={handleCloseDialog}
        id={id}
        clientId={client?.id}
      />
    </TableRow>
  );
};

CFDIIssuedRow.propTypes = {
  id: PropTypes.number.isRequired,
  createdAt: PropTypes.string.isRequired,
  folio: PropTypes.string.isRequired,
  series: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  amount: PropTypes.number.isRequired,
  iva: PropTypes.number.isRequired,
  amountIvaIncluded: PropTypes.number.isRequired,
  client: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    rfc: PropTypes.string.isRequired
  }),
  customerInfo: PropTypes.shape({
    legal_name: PropTypes.string.isRequired,
    tax_id: PropTypes.string.isRequired
  }),
  document: PropTypes.object,
  uuid: PropTypes.string,
  transaction: PropTypes.object,
  relatedDocuments: PropTypes.array,
  status: PropTypes.string
};

export default CFDIIssuedRow;
